/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `RegistrationEventCard` for the Caintra Connect frontend.
 * @module components/Events/EventListItem/RegistrationEventCard
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import dayjs from "dayjs";
import { truncateText } from "../../../utils/common.js";
import { ReactComponent as CalendarIcon } from "../../../assets/icons/Calendar_Today.svg";
import { ReactComponent as GroupIcon } from "../../../assets/icons/Group.svg";
import { ReactComponent as LocationIcon } from "../../../assets/icons/Location_on.svg";
import { ReactComponent as SalesIcon } from "../../../assets/icons/Sales.svg";
import { SvgIcon } from "@mui/material";
import { ReactComponent as CheckIcon } from "../../../assets/icons/Check.svg";
import {
  EventCardShell,
  EventDetailColumn,
  SingleEventDate,
} from "./shared.jsx";

const RegistrationEventCard = ({
  event,
  eventKey,
  user,
  t,
  styles,
  getEventCost,
  onViewRegistration,
  onRegister,
  onCancelAttendance,
}) => (
  <EventCardShell event={event} styles={styles} eventKey={eventKey}>
    <div className={styles.eventDetails}>
      <div className={styles.registrationEventDate}>
        <SingleEventDate eventDate={event.fechaEvento} styles={styles} />
        {event.isUserRegistered ? (
          <button
            className={styles.viewRegistrationButton}
            onClick={() => onViewRegistration(event)}
          >
            {t("pages.events.viewRegistration")}
          </button>
        ) : user?.tipoDeUsuario !== "12" ? (
          <button
            className={styles.registerButton}
            onClick={() => onRegister(event)}
          >
            {t("pages.events.register")}
          </button>
        ) : null}
      </div>
      <div className={styles.divider} />
      <div className={styles.eventDescriptionContainer}>
        <span className={styles.eventDescription}>
          {truncateText(event.descripcion, 50)}
        </span>
        {event.isUserRegistered && (
          <div className={styles.eventTags}>
            <div className={styles.registrationStatus}>
              <SvgIcon
                sx={{ fontSize: "14px", color: "#2A995A" }}
                component={CheckIcon}
              />
              {t("pages.events.registered")}
            </div>
            <button
              className={styles.cancelButton}
              disabled={user?.tipoDeUsuario === "12"}
              onClick={() => onCancelAttendance(event)}
            >
              {t("pages.events.cancelAttendance")}
            </button>
          </div>
        )}
      </div>
    </div>
    <div className={styles.eventAdditionalDetails}>
      {getEventCost(event) > 0 && (
        <EventDetailColumn
          icon={SalesIcon}
          label={t("pages.events.cost")}
          value={`$ ${getEventCost(event)} MXN`}
          styles={styles}
        />
      )}
      {event.cupoMaximo && (
        <EventDetailColumn
          icon={GroupIcon}
          label={t("pages.events.maximumCapacity")}
          value={`${event.cupoMaximo} ${t("pages.events.people")}`}
          styles={styles}
        />
      )}
      {event.fechaLimiteRegistro && (
        <EventDetailColumn
          icon={CalendarIcon}
          label={t("pages.events.dueDateRegistration")}
          value={dayjs(event.fechaLimiteRegistro).utc().format("LLL")}
          styles={styles}
        />
      )}
      {event.ubicacion && (
        <EventDetailColumn
          icon={LocationIcon}
          label={t("pages.events.location")}
          value={event.ubicacion}
          styles={styles}
        />
      )}
    </div>
  </EventCardShell>
);

export default RegistrationEventCard;
